import React, { useEffect, useState, useCallback } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, Wallet, ArrowDownCircle, ArrowUpCircle, Loader2, X, FileText, FileSpreadsheet } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function AdminAscReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exp, setExp] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/admin/asc/report", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const setToday = () => { setDateFrom(todayISO()); setDateTo(todayISO()); };
  const setThisMonth = () => { setDateFrom(monthStartISO()); setDateTo(todayISO()); };
  const clearRange = () => { setDateFrom(""); setDateTo(""); };

  const exportReport = async (format) => {
    setExp(format);
    try {
      const params = { format };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/admin/asc/report/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan_asc_${todayISO()}.${format === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
    finally { setExp(null); }
  };

  const s = data?.summary;
  const hasRange = dateFrom || dateTo;
  const cards = s ? [
    { label: "Jumlah Kartu", value: `${s.card_count} kartu`, sub: "Total kartu ASC terdaftar", cls: "text-stone-800", icon: <CreditCard size={18} className="text-stone-400" /> },
    { label: "Total Saldo", value: rupiah(s.total_balance), sub: "Saldo tersimpan saat ini (semua kartu)", cls: "text-emerald-700", icon: <Wallet size={18} className="text-emerald-500" /> },
    { label: "Total Top-Up", value: rupiah(s.topup_total), sub: `${s.topup_count} transaksi ${hasRange ? "dalam periode" : "keseluruhan"}`, cls: "text-sky-700", icon: <ArrowUpCircle size={18} className="text-sky-500" /> },
    { label: "Total Belanja", value: rupiah(s.purchase_total), sub: `${s.purchase_count} transaksi ${hasRange ? "dalam periode" : "keseluruhan"}`, cls: "text-amber-700", icon: <ArrowDownCircle size={18} className="text-amber-500" /> },
  ] : [];

  return (
    <div>
      <PageHeader title="Laporan Kartu ASC" subtitle="Rekap saldo tersimpan dan transaksi (top-up & belanja) seluruh kartu ASC. Saring per hari atau per bulan." />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="text-xs text-stone-500 block mb-1">Dari tanggal</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="asc-report-from" className="rounded-xl h-11 w-44" />
        </div>
        <div>
          <label className="text-xs text-stone-500 block mb-1">Sampai tanggal</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="asc-report-to" className="rounded-xl h-11 w-44" />
        </div>
        <Button variant="outline" onClick={setToday} data-testid="asc-report-today" className="rounded-xl h-11">Hari Ini</Button>
        <Button variant="outline" onClick={setThisMonth} data-testid="asc-report-month" className="rounded-xl h-11">Bulan Ini</Button>
        {hasRange && <Button variant="ghost" onClick={clearRange} className="rounded-xl h-11 text-stone-500"><X size={15} className="mr-1" /> Semua</Button>}
        <div className="flex-1" />
        <Button variant="outline" onClick={() => exportReport("pdf")} disabled={exp !== null} data-testid="asc-report-export-pdf" className="rounded-xl h-11">
          {exp === "pdf" ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <FileText size={15} className="mr-1.5" />} Ekspor PDF
        </Button>
        <Button variant="outline" onClick={() => exportReport("xlsx")} disabled={exp !== null} data-testid="asc-report-export-excel" className="rounded-xl h-11 text-emerald-700 border-emerald-200">
          {exp === "xlsx" ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <FileSpreadsheet size={15} className="mr-1.5" />} Ekspor Excel
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {cards.map((c) => (
              <div key={c.label} data-testid={`asc-report-card-${c.label}`} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs uppercase tracking-wide text-stone-400">{c.label}</span>
                  {c.icon}
                </div>
                <p className={`text-xl font-bold ${c.cls}`}>{c.value}</p>
                <p className="text-xs text-stone-400 mt-0.5">{c.sub}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
            <div className="px-4 py-3 border-b border-stone-100">
              <p className="font-heading font-semibold text-stone-800">Transaksi {hasRange ? "(sesuai periode)" : "(semua)"}</p>
            </div>
            {!data || data.transactions.length === 0 ? (
              <Empty><CreditCard className="mx-auto mb-2" /> Tidak ada transaksi.</Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Waktu</TableHead>
                    <TableHead>Nomor Kartu</TableHead>
                    <TableHead>Jenis</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                    <TableHead className="text-right">Saldo Setelah</TableHead>
                    <TableHead>Petugas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs whitespace-nowrap">{fmt(t.created_at)}</TableCell>
                      <TableCell className="font-mono text-sm">{t.card_number}</TableCell>
                      <TableCell>
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${t.type === "topup" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                          {t.type === "topup" ? "Top-up" : "Belanja"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{t.note}</TableCell>
                      <TableCell className={`text-right font-mono text-sm font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{rupiah(t.amount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-stone-600">{rupiah(t.balance_after)}</TableCell>
                      <TableCell className="text-sm text-stone-500">{t.actor || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
